import React, { useEffect, useState } from 'react';

import _ from 'lodash';
import { useDispatch } from 'react-redux';

import MouseMove from 'components/MouseMove';
import { ENUM_QUESTION_TYPE, ENUM_TYPE_ACTION } from 'constants/enum';
import { TEXT_COMMON, TEXT_CREATE_SURVEY } from 'constants/string/texts';
import useCreateSurvey from 'hooks/useCreateSurvey';
import Question from 'pages/Survey/Form/Question';
import QuestionType from 'pages/Survey/Form/QuestionType';
import Section from 'pages/Survey/Form/Section';
import SurveyTitle from 'pages/Survey/Form/SurveyTitle';
import { clearModal, setModal } from 'store/actions/common';
import { noneFunction } from 'utils/helper';

const Form = (props) => {
  const {
    form,
    sectionFocusing,
    setSectionFocusing,
    questionFocusing,
    setQuestionFocusing,
    viewOnly = false,
    isEdit = false,
    showControlMenu = true,
    showTitle = true,
    isShowVietName = false,
  } = props;
  const dispatch = useDispatch();

  const [draggedIndex, setDraggedIndex] = useState(null);
  const [draggedSectionIndex, setDraggedSectionIndex] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);

  const { onSaveStep } = useCreateSurvey();

  const getItemsNotDelete = (list_item) => {
    return list_item?.filter((item) => item.action !== ENUM_TYPE_ACTION.DELETE);
  };

  const getLargestId = (array, target, map = true) => {
    const allIds = map ? _.flatMap(array, target) : array;
    const largestId = _.maxBy(allIds, 'id');
    return largestId;
  };

  const handleAddSection = (callback) => {
    const sections = form?.values?.sections;
    const largestId = getLargestId(form?.values?.sections, '', false);

    const newSection = {
      title: '',
      id: Number(largestId?.id ?? 0) + 1,
      action: ENUM_TYPE_ACTION.CREATE,
      order: form.values.sections.length - 1,
      questions: [],
    };

    const sectionFocusingIndex = form?.values?.sections.findIndex((item) => item.id === sectionFocusing);

    if (sectionFocusingIndex === -1) {
      sections.push(newSection);
    } else {
      sections.splice(sectionFocusingIndex + 1, 0, newSection);
    }
    form.setFieldValue('sections', sections);
    setSectionFocusing(newSection.id);
    onSaveStep(form.values);

    if (callback) {
      const sectionIndex = _.findIndex(sections, { id: newSection.id });
      callback(sectionIndex);
    } else {
      setQuestionFocusing({});
    }
  };

  const generateNewQuestion = () => {
    const largestId = getLargestId(form?.values?.sections, 'questions');
    const question = {
      id: Number(largestId?.id ?? 0) + 1,
      title: '',
      action: ENUM_TYPE_ACTION.CREATE,
      is_required: false,
      question_type: ENUM_QUESTION_TYPE.DESCRIPTIVE_SHORT_ESSAY,
    };
    return question;
  };

  /**
   * @name handleAddQuestion
   * @description This function is used to add a new question to the form.
   *
   * If you are focusing on a section, the new question will be added to the top of the list of the focused section.
   * On the contrary, if you are focusing on a question, add a new question after the question you are focusing on.
   */
  const handleAddQuestion = async () => {
    // sectionFocus = id section focusing
    const addQuestion = (sectionFocus) => {
      const questionFocusingIndex = form.values?.sections?.[sectionFocus]?.questions?.findIndex((item) => item.id === questionFocusing.id);
      const sectionAdd = form?.values?.sections?.[sectionFocus]?.questions || [];

      const newQuestion = generateNewQuestion();

      if (!questionFocusing.id) {
        sectionAdd.unshift(newQuestion);
      } else {
        sectionAdd.splice(questionFocusingIndex + 1, 0, newQuestion);
      }

      form.setFieldValue(['sections', sectionFocus, 'questions'], sectionAdd);
      setQuestionFocusing(newQuestion);
      onSaveStep(form.values);
    };

    if (getItemsNotDelete(form?.values?.sections)?.length === 0) {
      await handleAddSection(addQuestion);
    } else {
      const sectionFocusingIndex = form?.values?.sections.findIndex((item) => item.id === sectionFocusing);
      await addQuestion(sectionFocusingIndex);
    }
  };

  const handleRequiredQuestion = async (sectionId, questionIndex, value) => {
    const sectionIndex = form.values.sections.findIndex((item) => item.id === sectionId);

    form.setFieldValue(['sections', sectionIndex, 'questions', questionIndex, 'is_required'], value);
    const question = form.values.sections[sectionIndex].questions[questionIndex];
    question.is_required = value;
    onSaveStep(form.values);
  };

  const handleDuplicateQuestion = async (sectionId, questionIndex) => {
    const sectionIndex = form.values.sections.findIndex((item) => item.id === sectionId);

    const sectionUpdated = form.values.sections[sectionIndex].questions;
    const largestId = getLargestId(form?.values?.sections, 'questions');

    const newQuestion = {
      ...sectionUpdated?.[questionIndex],
      action: ENUM_TYPE_ACTION.CREATE,
      id: Number(largestId?.id ?? 0) + 1,
    };
    sectionUpdated.splice(questionIndex + 1, 0, newQuestion);

    setSectionFocusing(sectionId);
    setQuestionFocusing(newQuestion);

    // Handle set data
    form.setFieldValue(['sections', sectionIndex, 'questions'], sectionUpdated);

    const question = form.values.sections;
    question[sectionIndex].questions = sectionUpdated;
    onSaveStep(form.values);
  };

  const handleDeleteQuestion = async (sectionId, questionId) => {
    const sectionIndex = form.values.sections.findIndex((item) => item.id === sectionId);
    const questionIndex = form.values.sections[sectionIndex]?.questions.findIndex((item) => item.id === questionId);
    const sectionFocusingIndex = form.values.sections.findIndex((item) => item.id === sectionFocusing);

    const sectionDelete = form.values.sections[sectionIndex];

    const updatePositionQuestionFocus = () => {
      const question = form.values.sections;
      question[sectionIndex] = sectionDelete;

      if (sectionIndex !== sectionFocusingIndex) return;

      if (getItemsNotDelete(question[sectionIndex].questions).length <= 0) {
        setQuestionFocusing({});
        return true;
      }

      // Focus in the first item if the section has the opposite question in the section
      if (getItemsNotDelete(question[sectionIndex].questions).length > 0) {
        setQuestionFocusing(getItemsNotDelete(question[sectionIndex].questions)?.[0]);
      } else {
        setQuestionFocusing({});
      }

      return true;
    };

    // Delete
    if (sectionDelete.questions[questionIndex].action === ENUM_TYPE_ACTION.CREATE) {
      sectionDelete.questions.splice(questionIndex, 1);
    } else {
      sectionDelete.questions[questionIndex]['action'] = ENUM_TYPE_ACTION.DELETE;
    }

    // Update form
    form.setFieldValue(['sections', sectionIndex], { ...sectionDelete });

    // Update position of question focus if it's in the deleted section
    await updatePositionQuestionFocus();
    onSaveStep(form.values);
  };

  const handleControlQuestion = (sectionId, questionIndex, action) => {
    const sectionIndex = form.values.sections.findIndex((item) => item.id === sectionId);
    const questionUpdated = Object.assign({}, form.values?.sections?.[sectionIndex]?.questions?.[questionIndex]);

    let currentCheckbox = _.cloneDeep(questionUpdated?.active_checkbox || []) || [];

    if (currentCheckbox?.indexOf(action) === -1) {
      currentCheckbox.push(action);
    } else {
      currentCheckbox = currentCheckbox.filter((item) => item !== action);
    }

    // Update
    form.setFieldValue(['sections', sectionIndex, 'questions', questionIndex, 'active_checkbox'], currentCheckbox);
    const question = form.values.sections[sectionIndex].questions[questionIndex];
    question.active_checkbox = currentCheckbox;
    onSaveStep(form.values);
  };

  const onClickDeleteSection = (sectionId) => {
    const sectionIndex = _.findIndex(form.values.sections, { id: sectionId });

    const handleDeleteSection = async () => {
      const sectionUpdated = form.values.sections;

      // Update Position Focus for section
      const updatePositionSectionFocus = async () => {
        const survey = form.values;
        survey.sections = sectionUpdated;

        const sectionNoDelete = await getItemsNotDelete(survey.sections);

        if (sectionIndex > sectionNoDelete.length - 1) {
          setSectionFocusing(sectionNoDelete[sectionNoDelete.length - 1]?.id || -1);
          return true;
        } else {
          setSectionFocusing(sectionNoDelete[sectionIndex]?.id || -1);
          return true;
        }
      };

      // Delete section
      if (sectionUpdated[sectionIndex].action === ENUM_TYPE_ACTION.CREATE) {
        sectionUpdated.splice(sectionIndex, 1);
      } else {
        sectionUpdated[sectionIndex].action = ENUM_TYPE_ACTION.DELETE;
      }

      // Update form
      form.setFieldValue('sections', sectionUpdated);
      // Update position section
      await updatePositionSectionFocus();
      onSaveStep(form.values);
      dispatch(clearModal());
    };

    dispatch(
      setModal({
        isOpen: true,
        title: TEXT_CREATE_SURVEY.CONFIRM_DELETE_SECTION,
        type: 'delete',
        confirmText: TEXT_COMMON.DELETE,
        cancelText: TEXT_COMMON.CANCEL_TEXT,
        onConfirm: handleDeleteSection,
        onCancel: () => {
          dispatch(clearModal());
        },
      })
    );
  };

  const onCloneSection = async (sectionId) => {
    const sectionIndex = _.findIndex(form.values.sections, { id: sectionId });

    const sections = form.values.sections;

    const newSection = _.cloneDeep(form.values.sections[sectionIndex]);

    newSection.action = ENUM_TYPE_ACTION.CREATE;

    const largestIdSection = getLargestId(form?.values?.sections, '', false);
    newSection.id = Number(largestIdSection?.id ?? 0) + 1;

    let largestIdQuestion = (await getLargestId(form?.values?.sections, 'questions')?.id) || 1;
    await newSection.questions.map((_, questionIndex) => {
      largestIdQuestion = Number(largestIdQuestion) + 1;
      newSection.questions[questionIndex].action = ENUM_TYPE_ACTION.CREATE;
      newSection.questions[questionIndex].id = Number(largestIdQuestion);
    });

    sections.splice(sectionIndex + 1, 0, newSection);
    form.setFieldValue('sections', sections);
    setSectionFocusing(newSection.id);
    onSaveStep(form.values);
  };

  /**
   * Event Drag/Drop Question
   */
  const handleDragStart = (e, sectionIndex, index) => {
    setDraggedIndex(index);
    setDraggedSectionIndex(sectionIndex);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = (e, sectionIndex) => {
    e.preventDefault();
    resetDragAndDrop();
    if (draggedSectionIndex !== null && dropIndex !== null) {
      const updatedSection = form?.values?.sections;
      const [movedItem] = updatedSection[draggedSectionIndex].questions.splice(draggedIndex, 1);
      updatedSection?.[sectionIndex]?.questions?.splice(dropIndex, 0, movedItem);
      form.setFieldValue(['sections'], updatedSection);
    }
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDropIndex(index);
  };

  const resetDragAndDrop = () => {
    setDraggedIndex(null);
    setDraggedSectionIndex(null);
    setDropIndex(null);
  };

  /**
   * End Event Drag/Drop Question
   */

  useEffect(() => {
    if (sectionFocusing === -1) {
      setSectionFocusing(form.values.sections[0]?.id || -1);
    }
  }, [form.values]);

  return (
    <div className="relative">
      {!viewOnly && (
        <div className="mb-8">
          <QuestionType isEdit={isEdit} form={form} />
        </div>
      )}
      {showTitle && <SurveyTitle viewOnly={viewOnly} form={form} />}
      <div className={`relative questions ${showTitle ? 'mt-9' : ''}`}>
        <MouseMove
          viewOnly={viewOnly}
          onClickSplit={() => handleAddSection()}
          onClickAdd={() => handleAddQuestion()}
          showControlMenu={showControlMenu}
        >
          <div className="w-full grid grid-cols-1 gap-8">
            {getItemsNotDelete(form.values?.sections)?.map((section, sectionIndex) => (
              <section id={`sections-${sectionIndex}`} key={`sections-${sectionIndex}`} className="w-full grid grid-cols-1 gap-4">
                <Section
                  viewOnly={viewOnly}
                  isFocusing={sectionFocusing === section?.id && questionFocusing?.id === undefined}
                  form={form}
                  data={section}
                  index={sectionIndex}
                  clickSection={() => {
                    setSectionFocusing(section?.id);
                    setQuestionFocusing({});
                  }}
                  onDeleteSection={onClickDeleteSection}
                  onCloneSection={onCloneSection}
                />
                {getItemsNotDelete(section.questions)?.map((question, questionIndex) =>
                  viewOnly ? (
                    <div key={`question-${question.id}`} id={`questions-${sectionIndex}${questionIndex}`}>
                      <Question
                        viewOnly={viewOnly}
                        form={form}
                        questionEditing={false}
                        data={question}
                        section={section}
                        sectionIndex={sectionIndex}
                        isShowVietName={isShowVietName}
                        questionIndex={questionIndex}
                        handleDuplicateQuestion={noneFunction}
                        handleDeleteQuestion={noneFunction}
                        handleControlQuestion={noneFunction}
                        handleRequiredQuestion={noneFunction}
                      />
                    </div>
                  ) : (
                    <div
                      key={question.id}
                      id={`questions-${sectionIndex}${questionIndex}`}
                      draggable
                      onDragStart={(e) => {
                        const sectionIndexHandle = form.values.sections.findIndex((item) => item.id === section.id);
                        handleDragStart(e, sectionIndexHandle, questionIndex);
                      }}
                      onDragOver={(e) => handleDragOver(e, questionIndex)}
                      onDrop={(e) => {
                        const sectionIndexHandle = form.values.sections.findIndex((item) => item.id === section.id);
                        handleDrop(e, sectionIndexHandle);
                      }}
                      onClick={() => {
                        setQuestionFocusing(question);
                        setSectionFocusing(section?.id);
                      }}
                    >
                      <Question
                        viewOnly={viewOnly}
                        form={form}
                        questionEditing={question?.id === questionFocusing?.id}
                        data={question}
                        section={section}
                        sectionIndex={sectionIndex}
                        questionIndex={questionIndex}
                        handleDuplicateQuestion={handleDuplicateQuestion}
                        handleDeleteQuestion={handleDeleteQuestion}
                        handleControlQuestion={handleControlQuestion}
                        handleRequiredQuestion={handleRequiredQuestion}
                      />
                    </div>
                  )
                )}

                {/* The space to release Question in case the Section has no Question */}
                {viewOnly ? (
                  <></>
                ) : (
                  <>
                    {getItemsNotDelete(section.questions)?.length === 0 && (
                      <div
                        style={{ minHeight: '50px' }}
                        onDragOver={(e) => handleDragOver(e, sectionIndex, 0)}
                        onDrop={(e) => handleDrop(e, sectionIndex)}
                      >
                        <span className="text-primary-405/80"></span>
                      </div>
                    )}
                  </>
                )}
              </section>
            ))}
          </div>
        </MouseMove>
      </div>
    </div>
  );
};

export default React.memo(Form);
